"use client"

import { useContext, useRef, useState } from "react"
import axios from "axios"
import { AppContext } from "../contexts/AppContext"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Button,
  TextField,
  Typography,
  Divider,
  Box,
  Alert,
  Paper,
  Stack,
  alpha,
  useTheme,
  InputAdornment,
  Fade,
  Chip,
  Tooltip,
} from "@mui/material"
import {
  Store as StoreIcon,
  Domain as DomainIcon,
  Key as KeyIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from "@mui/icons-material"

const NewStoreDialog = ({ data, onChange, onCancel }) => {
  const isUpdating = data?.id > 0
  const { state } = useContext(AppContext)
  const [storeName, setStoreName] = useState(data?.name || null)
  const [sellsabeelDomain, setSellsabeelDomain] = useState(data?.sellsabeel_domain || null)
  const [apiKey, setApiKey] = useState(data?.sellsabeel_api_key || null)
  const [validationError, setValidationError] = useState(null)
  const theme = useTheme()

  const domainRef = useRef(null)
  const keyRef = useRef(null)
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (userData) => {
      const url = isUpdating ? `/api/admin/stores/${data.id}` : `/api/admin/stores/create`
      const method = isUpdating ? "put" : "post"

      const response = await axios({
        method,
        url,
        data: userData,
        headers: {
          Authorization: state.token ? `Bearer ${state.token}` : "",
        },
      })

      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["stores"])
      if (onChange) onChange()
    },
    onError: (error) => {
      console.error("Error saving store:", error)
      setValidationError("Failed to save store. Please try again.")
    },
  })

  const handleSubmit = () => {
    // Validate inputs
    if (!storeName) {
      setValidationError("A store name is required.")
      return
    }
    // if the is sellsabeelDomain you must have apiKey
    if (sellsabeelDomain && !apiKey) {
      setValidationError("API key is required if Sellsabeel domain is provided.")
      return
    }

    // validate domain https://example.sellsabeel.com

    
    //const domainRegex = /^[a-zA-Z0-9-]+\.sellsabeel\.com$/
    const domainRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)?sellsabeel\.com$/
    if (sellsabeelDomain && !domainRegex.test(sellsabeelDomain)) {
      setValidationError("Please enter a valid Sellsabeel domain (e.g., example.sellsabeel.com).")
      return
    }

    const userData = {
      name: storeName,
      sellsabeel_domain: sellsabeelDomain,
      sellsabeel_api_key: apiKey,
    }

    console.log("Submitting store data:", userData)
    mutation.mutate(userData)
  }

  return (
    <Fade in={true} timeout={400}>
      <Box className="flex justify-center items-center mt-8 px-4">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            width: "100%",
            maxWidth: "600px",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
        >
          {/* Decorative top accent */}
          <Box
            sx={{
              height: 8,
              width: "100%",
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(
                theme.palette.secondary.main,
                0.8,
              )})`,
            }}
          />

          {/* Header */}
          <Box
            sx={{
              p: 4,
              pb: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
                theme.palette.primary.light,
                0.1,
              )} 100%)`,
              borderBottom: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.1),
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              <StoreIcon sx={{ fontSize: 28, color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 0.5,
                }}
              >
                {isUpdating ? "Update Store" : "Create New Store"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isUpdating
                  ? "Update your store information and settings"
                  : "Enter the details to create a new store in CardzHub"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            {validationError && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`,
                }}
                onClose={() => setValidationError(null)}
              >
                {validationError}
              </Alert>
            )}

            <Stack spacing={3}>
              {/* Store Name */}
              <TextField
                label="Store Name"
                variant="outlined"
                autoFocus
                fullWidth
                value={storeName || ""}
                onChange={(e) => {
                  if (e.target.value === "") {
                    setStoreName(null)
                  } else {
                    setStoreName(e.target.value)
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <StoreIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <Divider>
                <Chip
                  icon={<DomainIcon fontSize="small" />}
                  label="Sellsabeel Integration"
                  variant="outlined"
                  size="small"
                />
              </Divider>

              {/* Domain */}
              <TextField
                label="Sellsabeel Domain"
                variant="outlined"
                fullWidth
                inputRef={domainRef}
                value={sellsabeelDomain || ""}
                placeholder="example.sellsabeel.com"
                onChange={(e) => {
                  if (e.target.value === "") {
                    setSellsabeelDomain(null)
                  } else {
                    setSellsabeelDomain(e.target.value)
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DomainIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                helperText="Enter your Sellsabeel domain (optional)"
              />

              {/* API Key */}
              <TextField
                label="Sellsabeel API Key"
                variant="outlined"
                fullWidth
                disabled={!storeName || !sellsabeelDomain}
                inputRef={keyRef}
                value={apiKey || ""}
                type="password"
                onChange={(e) => {
                  if (e.target.value === "") {
                    setApiKey(null)
                  } else {
                    setApiKey(e.target.value)
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <KeyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                helperText={
                  !storeName || !sellsabeelDomain
                    ? "Enter store name and domain first"
                    : "Required if domain is provided"
                }
              />

              {/* Info Box */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.2),
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <InfoIcon color="info" sx={{ mr: 1.5, mt: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  Sellsabeel integration allows you to connect your CardzHub account with your Sellsabeel store. This
                  enables automatic synchronization of customer data and loyalty points.
                </Typography>
              </Paper>

              {/* Buttons */}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  fullWidth
                  onClick={() => onCancel(false)}
                  startIcon={<CancelIcon />}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    borderColor: alpha(theme.palette.divider, 0.5),
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      borderColor: theme.palette.text.secondary,
                      bgcolor: alpha(theme.palette.divider, 0.1),
                    },
                  }}
                >
                  Cancel
                </Button>
                <Tooltip title={!storeName ? "Store name is required" : ""}>
                  <span style={{ width: "100%" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={mutation.isPending || !storeName}
                      startIcon={<SaveIcon />}
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        "&:hover": {
                          boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                      }}
                    >
                      {mutation.isPending ? "Saving..." : isUpdating ? "Update Store" : "Create Store"}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Fade>
  )
}

export default NewStoreDialog
